import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, User, Phone, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import LanguageLogo, { LotusIcon } from '@/components/Logo';
import { MEDIA } from '@/data/plotData';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SiteVisitForm() {
  const { lang } = useLanguage();
  const t = translations[lang].siteVisit;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError(t.errorRequired);
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\s/g, ''))) {
      setError(t.errorPhone);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/site-visits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          preferred_date: date ? format(date, 'yyyy-MM-dd') : null,
          source: 'website_form',
        }),
      });
      if (res.ok) {
        setSuccess(true);
        setName('');
        setPhone('');
        setDate(undefined);
      } else {
        setError(t.errorGeneric);
      }
    } catch {
      setError(t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="site-visit" data-testid="site-visit-section" className="py-16 sm:py-24 bg-[#2C4C3B] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Image + text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
              {t.sectionTag}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 tracking-tight">
              {t.title}
            </h2>
            <p className="text-white/70 text-base mt-4 max-w-lg leading-relaxed">
              {t.subtitle}
            </p>
            {/* Image preview with disclaimer */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-white/10 hidden lg:block relative">
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_redhills-villas/artifacts/totsmlcx_Lakshmi%20Nivasa%20Site%20Visit.jpg"
                alt="Lakshmi Nivasa villa community render"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <LanguageLogo lang={lang} variant="white" size="sm" />
                <p className="text-white/60 text-[10px] mt-2 leading-relaxed italic">
                  {t.imageDisclaimer}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            {success ? (
              <div data-testid="site-visit-success" className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 sm:p-10 border border-white/15 text-center">
                <CheckCircle2 className="w-14 h-14 text-[#D48C57] mx-auto mb-4" />
                <h3 className="font-serif text-2xl font-bold text-white">{t.successTitle}</h3>
                <p className="text-white/70 mt-2 text-sm">{t.successMsg}</p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="site-visit-another"
                  className="mt-6 px-6 py-2.5 rounded-full bg-[#D48C57] hover:bg-[#C07A4A] text-white text-sm font-medium transition-all duration-200"
                >
                  {t.bookAnother}
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="site-visit-form"
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/15 space-y-5"
              >
                <h3 className="font-serif text-xl font-bold text-white mb-1">{t.formTitle}</h3>
                <p className="text-white/60 text-xs mb-4">{t.formSubtitle}</p>

                {/* Name */}
                <div className="space-y-1.5">
                  <label className="text-white/80 text-xs font-medium flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {t.nameLabel}
                  </label>
                  <Input
                    data-testid="site-visit-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.namePlaceholder}
                    className="bg-white/10 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#D48C57] h-11"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="text-white/80 text-xs font-medium flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {t.phoneLabel}
                  </label>
                  <Input
                    data-testid="site-visit-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t.phonePlaceholder}
                    type="tel"
                    className="bg-white/10 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#D48C57] h-11"
                  />
                </div>

                {/* Date Picker */}
                <div className="space-y-1.5">
                  <label className="text-white/80 text-xs font-medium flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {t.dateLabel}
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="site-visit-date-trigger"
                        type="button"
                        className="flex h-11 w-full items-center rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm text-white/80 hover:bg-white/15 transition-colors"
                      >
                        <CalendarDays className="w-4 h-4 mr-2 text-white/40" />
                        {date ? format(date, 'PPP') : t.datePlaceholder}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#FDFBF7] border-[#E5DFD3]" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date()}
                        data-testid="site-visit-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {error && (
                  <p data-testid="site-visit-error" className="text-red-300 text-xs">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="site-visit-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#D48C57] hover:bg-[#C07A4A] disabled:opacity-60 text-white py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#D48C57]/20"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {loading ? t.submitting : t.submitBtn}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
